
import {
  computed,
  defineComponent,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  PropType,
  ref,
  watch,
} from 'vue'
import BaseInputOutlined from '../inputs/BaseInputOutlined.vue'
import CustomActivatorSelect from '../inputs/CustomActivatorSelect.vue'
import SmallHint from '@/components/SmallHint.vue'
import UserIcon from '@/assets/vuetifyIcons/user.vue'
import UserGroupIcon from '@/assets/vuetifyIcons/userGroup.vue'
import SpinArrows from '@/assets/vuetifyIcons/spinArrows.vue'
import { formatPhoneNumber } from '@/helpers/formatters/phoneFormatter'
import phoneValidator, { isValidPhone } from '@/helpers/validators/phoneValidator'
import tgGroupIdValidator, { cleanTgGroupId, isValidTgGroupId } from '@/helpers/validators/tgGroupIdValidator'
import tgUsernameValidator, { isValidTgUsername, stripTgUsernameWhitespace, tgUsernameWithoutAt } from '@/helpers/validators/tgUsernameValidator'

type ContactInputKind = 'empty' | 'phone' | 'tg_user' | 'tg_group' | 'unknown'

const MOBILE_BREAKPOINT = 960
const ADDED_TOAST_MS = 3000

export default defineComponent({
  name: 'PhoneOrTgInputGroup',
  components: { BaseInputOutlined, CustomActivatorSelect, SmallHint, UserIcon, UserGroupIcon, SpinArrows },
  props: {
    appearance: {
      type: String as PropType<'default' | 'starter-pack'>,
      default: 'default',
    },
    value: {
      type: [Array, String] as PropType<string[] | string>,
      default: '',
    },
    placeholderPhone: { type: String, default: '+7 (999) 123-45-67' },
    placeholderTg: { type: String, default: 'ID группы Telegram (например -1001234567890)' },
    placeholderNeutral: {
      type: String,
      default: 'Телефон / @Никнейм / ID группы',
    },
    labelTgUsername: { type: String, default: 'Telegram username' },
    /** Подпись поля ID группы; по умолчанию берётся из `placeholderTg`. */
    labelTgGroup: { type: String, default: undefined },
    placeholderTgUsername: { type: String, default: '@username' },
    canDeleteFirstItem: { type: Boolean, default: false },
    color: { type: String, default: undefined },
    validator: { type: Function },
    phoneValidator: { type: Function as PropType<(v: string) => string | undefined>, default: phoneValidator },
    readonly: { type: Boolean, default: false },
    canManageContacts: { type: Boolean, default: true },
    showAddFromAllGroups: { type: Boolean, default: false },
    addFromAllGroupsItems: {
      type: Array as PropType<{ id: string | number; name: string }[]>,
      default: () => [],
    },
    itemsMeta: {
      type: Array as PropType<{
        id?: string | null
        contactValue?: string | null
        displayName?: string | null
        contactType?: string | null
        isVerified?: boolean | null
        isVerificationChecked?: boolean
        pendingRemovalId?: string | null
        /** Ошибка сети/запроса при последней попытке проверки */
        verificationRequestFailed?: boolean
      }[]>,
      default: () => []
    },
    /** Индексы строк с активным запросом «Проверить». */
    verifyingIndices: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
    /** Заголовок списка добавленных контактов (мобильный UI). */
    savedListTitle: {
      type: String,
      default: 'Получатели уведомлений',
    },
  },
  emits: ['input', 'verify', 'remove'],
  setup(props, { emit }) {
    const root = getCurrentInstance()?.proxy
    const localValue = ref<string[]>([])
    const inputRefsByIndex = ref<Record<number, any>>({})
    const itemKeys = ref<string[]>([])
    const showAddedToast = ref(false)
    const pendingToastIndex = ref<number | null>(null)
    /** Пользователь активно редактирует черновик (не трогаем его при bootstrap списка). */
    const mobileDraftActive = ref(false)
    let addedToastTimer: ReturnType<typeof setTimeout> | null = null
    let ensuringDraft = false

    const isMobileLayout = computed(
      () => (root?.$vuetify?.breakpoint?.width || 0) < MOBILE_BREAKPOINT
    )

    const tryParseJsonArray = (raw: string) => {
      if (!raw.trim().startsWith('[')) return null
      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) return parsed.map((v) => String(v ?? '')).filter((v) => v !== undefined)
      } catch {
        // Игнорируем ошибки парсинга.
      }
      return null
    }

    const normalizeIncoming = (incoming: string[] | string | undefined | null): string[] => {
      if (Array.isArray(incoming)) return incoming.map((v) => String(v ?? '')).filter((v) => v !== undefined)
      if (typeof incoming === 'string') {
        const v = incoming.trim()
        if (!v) return []
        const parsed = tryParseJsonArray(v)
        if (parsed) return parsed
        return [v]
      }
      return []
    }

    const isInputReadonly = computed(() => props.readonly || !props.canManageContacts)

    const verifyingIndexList = computed(() =>
      (Array.isArray(props.verifyingIndices) ? props.verifyingIndices : []).filter(
        (i): i is number => typeof i === 'number' && Number.isInteger(i) && i >= 0
      )
    )

    const isAnyRowVerifying = computed(() => verifyingIndexList.value.length > 0)

    const isRowVerifying = (index: number) => verifyingIndexList.value.includes(index)

    const isRowInputReadonly = (index: number) =>
      props.readonly || !props.canManageContacts || isRowVerifying(index)

    const serializeOutgoing = (arr: string[]) =>
      (arr || [])
        .filter((v) => v !== undefined && v !== null)
        .map((v) => String(v ?? ''))

    const updateKeys = (removedIndex: number | null = null) => {
      if (removedIndex !== null) {
        itemKeys.value.splice(removedIndex, 1)
      } else {
        itemKeys.value = localValue.value.map((_, index) => {
          return itemKeys.value[index] || `phone-or-tg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${index}`
        })
      }
    }

    watch(
      () => props.value,
      (newValue) => {
        const normalized = normalizeIncoming(newValue)
        if (JSON.stringify(localValue.value) !== JSON.stringify(normalized)) {
          localValue.value = normalized
          updateKeys()
        }
      },
      { immediate: true, deep: true }
    )

    // Инициализируем localValue даже до первого срабатывания watch.
    const initNormalized = normalizeIncoming(props.value)
    localValue.value = initNormalized
    if (itemKeys.value.length === 0) updateKeys()

    const UNKNOWN_FORMAT_MESSAGE =
      'Некорректное значение'

    const getInputKind = (raw: string): ContactInputKind => {
      const t = String(raw ?? '').trim()
      if (!t) return 'empty'
      const first = t[0]
      if (first === '+' || (first >= '0' && first <= '9')) return 'phone'
      if (first === '-') return 'tg_group'
      if (first === '@' || /[a-zA-Z]/.test(first)) return 'tg_user'
      return 'unknown'
    }

    /** Цифры и один ведущий «+» (для хранения и ввода). */
    const sanitizePhoneInput = (raw: string) => {
      const s = String(raw ?? '').replace(/\s/g, '')
      if (!s) return ''
      const hasLeadingPlus = s[0] === '+'
      const digits = s.replace(/\D/g, '')
      if (!digits && hasLeadingPlus) return '+'
      return hasLeadingPlus ? `+${digits}` : digits
    }

    const normalizePhoneForStore = (raw: string) => {
      const compact = sanitizePhoneInput(String(raw ?? '').trim())
      if (!compact) return ''
      if (compact === '+') return '+'
      return compact.replace(/^\+8/, '+7')
    }

    const hasPhoneDigits = (raw: string) => /\d/.test(sanitizePhoneInput(raw))

    const normalizeForEmit = (raw: string, kind: ContactInputKind) => {
      const trimmed = String(raw ?? '').trim()
      if (!trimmed) return ''
      if (kind === 'tg_group') return cleanTgGroupId(trimmed)
      if (kind === 'unknown') return trimmed.replace(/\s/g, '')
      if (kind === 'tg_user') return stripTgUsernameWhitespace(trimmed)
      if (kind === 'phone') return normalizePhoneForStore(trimmed)
      return trimmed
    }

    /** Каноническое значение для сравнения с бэкендом и «последней проверкой». */
    const canonicalFromRaw = (raw: string) => {
      const trimmed = String(raw ?? '').trim()
      if (!trimmed) return ''
      const kind = getInputKind(trimmed)
      if (kind === 'empty') return ''
      if (kind === 'unknown') return normalizeForEmit(String(raw ?? ''), 'unknown')
      return normalizeForEmit(String(raw ?? ''), kind)
    }

    const formatPhoneDisplay = (raw: string) => {
      const compact = String(raw ?? '').trim().replace(/[^\d+]/g, '')
      if (!compact) return ''
      if (compact === '+') return '+'
      let normalized = compact
      if (!normalized.startsWith('+')) {
        if (normalized.startsWith('8')) normalized = `+7${normalized.slice(1)}`
        else if (normalized.startsWith('7')) normalized = `+${normalized}`
      } else if (normalized.startsWith('+8')) {
        normalized = `+7${normalized.slice(2)}`
      }
      return formatPhoneNumber(normalized)
    }

    const getInputDisplayValue = (item: string) => {
      const kind = getInputKind(item)
      if (kind === 'phone') return formatPhoneDisplay(item)
      return item
    }

    const getInputLabel = (item: string) => {
      const kind = getInputKind(item)
      if (kind === 'phone') return props.placeholderPhone
      if (kind === 'tg_user') return props.labelTgUsername
      if (kind === 'tg_group') return props.labelTgGroup || props.placeholderTg
      return props.placeholderNeutral
    }

    const setInputRef = (index: number, el: any) => {
      if (el) {
        inputRefsByIndex.value[index] = el
      } else {
        delete inputRefsByIndex.value[index]
      }
    }

    const resetInputValidation = (index: number) => {
      nextTick(() => {
        const inputRef = inputRefsByIndex.value[index]
        const textField = inputRef?.$children?.find?.((child: any) => typeof child?.resetValidation === 'function')
        textField?.resetValidation?.()
      })
    }

    const focusInputByIndex = (index: number, retries = 6) => {
      const tryFocus = (attempt: number) => {
        const inputRef = inputRefsByIndex.value[index]
        const inputEl: HTMLInputElement | null = inputRef?.$el?.querySelector('input') || null
        if (inputEl) {
          inputEl.focus()
          const caretPos = inputEl.value?.length ?? 0
          inputEl.setSelectionRange?.(caretPos, caretPos)
          return
        }
        if (attempt < retries) {
          setTimeout(() => tryFocus(attempt + 1), 0)
        }
      }
      nextTick(() => tryFocus(0))
    }

    const focusInputAt = (index: number) => {
      focusInputByIndex(index)
    }

    const sanitizeFieldInput = (raw: string, kind: ContactInputKind) => {
      const withoutSpaces = String(raw ?? '').replace(/\s/g, '')
      if (kind === 'phone') {
        return sanitizePhoneInput(withoutSpaces)
      }
      return withoutSpaces
    }

    /** Полная замена отформатированного номера другим типом контакта (@, -, буква). */
    const isNonPhoneStarterKey = (key: string) =>
      key === '@' || key === '-' || (key.length === 1 && /[a-zA-Z]/.test(key))

    const isReplacingEntireInput = (target: HTMLInputElement | null) => {
      if (!target) return false
      const value = target.value || ''
      if (!value.length) return false
      const start = target.selectionStart ?? 0
      const end = target.selectionEnd ?? 0
      return start === 0 && end === value.length
    }

    const getCurrentInputValue = (index: number, e: KeyboardEvent) => {
      const target = e.target as HTMLInputElement | null
      if (target) return target.value || ''
      return localValue.value[index] || ''
    }

    const updateItem = (index: number, raw: string) => {
      if (isInputReadonly.value) return
      const withoutSpaces = String(raw ?? '').replace(/\s/g, '')
      const kind = getInputKind(withoutSpaces.trim())
      const sanitized = sanitizeFieldInput(withoutSpaces, kind)
      const trimmed = sanitized.trim()
      const finalKind = getInputKind(trimmed)
      const cleaned = trimmed ? normalizeForEmit(sanitized, finalKind) : ''
      localValue.value.splice(index, 1, cleaned)
      if (isMobileLayout.value && index === localValue.value.length - 1) {
        // Любой ввод в последнюю строку = активный черновик (не уезжает в список снизу).
        mobileDraftActive.value = Boolean(cleaned)
      }
      if (!cleaned) {
        clearBlurAutoVerifyTimer(index)
        resetInputValidation(index)
      }
      emit('input', serializeOutgoing(localValue.value))
    }

    const normalizeValue = (v: string) => String(v ?? '').trim()

    const BLUR_AUTO_VERIFY_MS = 1000
    const lastVerifiedCanonicalByIndex = ref<Record<number, string>>({})
    const blurAutoVerifyTimers: Record<number, ReturnType<typeof setTimeout>> = {}
    let skipBlurAutoVerifyRowIndex: number | null = null
    let skipBlurAutoVerifyClearTimer: ReturnType<typeof setTimeout> | null = null

    const clearBlurAutoVerifyTimer = (index: number) => {
      const t = blurAutoVerifyTimers[index]
      if (t != null) {
        clearTimeout(t)
        delete blurAutoVerifyTimers[index]
      }
    }

    const clearAllBlurAutoVerifyTimers = () => {
      Object.keys(blurAutoVerifyTimers).forEach((k) => clearBlurAutoVerifyTimer(Number(k)))
    }

    const tryEmitBlurAutoVerify = (index: number) => {
      const rowItem = localValue.value[index] ?? ''
      if (!props.canManageContacts) return
      if (getItemMeta(index).verificationState === true) return
      const rowCanon = getCanonicalValueForRow(index)
      const rowLast = lastVerifiedCanonicalByIndex.value[index]
      if (rowLast !== undefined && rowCanon === rowLast) return
      if (!canVerifyItem(rowItem, index)) return
      if (isRowVerifying(index)) return
      emit('verify', index)
    }

    /** Сразу запускает отложенную автопроверку (иначе add/clear её отменяет). */
    const flushBlurAutoVerifyTimers = () => {
      Object.keys(blurAutoVerifyTimers).forEach((k) => {
        const index = Number(k)
        clearBlurAutoVerifyTimer(index)
        tryEmitBlurAutoVerify(index)
      })
    }

    const onAddActionPointerDown = () => {
      nextTick(() => flushBlurAutoVerifyTimers())
    }

    const shiftLastVerifiedAfterRemove = (removedIndex: number) => {
      const prev = lastVerifiedCanonicalByIndex.value
      const next: Record<number, string> = {}
      Object.keys(prev).forEach((k) => {
        const idx = Number(k)
        if (Number.isNaN(idx)) return
        if (idx < removedIndex) next[idx] = prev[idx]
        else if (idx > removedIndex) next[idx - 1] = prev[idx]
      })
      lastVerifiedCanonicalByIndex.value = next
    }

    const pruneLastVerifiedKey = (index: number) => {
      if (!(index in lastVerifiedCanonicalByIndex.value)) return
      const next = { ...lastVerifiedCanonicalByIndex.value }
      delete next[index]
      lastVerifiedCanonicalByIndex.value = next
    }

    const removeItem = (index: number) => {
      if (!props.canManageContacts) return
      if (index === 0 && !props.canDeleteFirstItem) return
      clearAllBlurAutoVerifyTimers()
      emit('remove', index)
      let replacedSoleRow = false
      if (localValue.value.length === 1) {
        localValue.value.splice(0, 1, '')
        itemKeys.value = [`phone-or-tg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-0`]
        pruneLastVerifiedKey(0)
        replacedSoleRow = true
      } else {
        localValue.value.splice(index, 1)
        updateKeys(index)
        shiftLastVerifiedAfterRemove(index)
      }
      emit('input', serializeOutgoing(localValue.value))
      if (replacedSoleRow) {
        nextTick(() => focusInputAt(0))
      }
    }

    const addItem = () => {
      if (!props.canManageContacts) return
      flushBlurAutoVerifyTimers()
      localValue.value.push('')
      updateKeys()
      emit('input', serializeOutgoing(localValue.value))

      nextTick(() => {
        const newIndex = localValue.value.length - 1
        focusInputAt(newIndex)
      })
    }

    const addGroupFromSelect = (groupId: string | number | null) => {
      if (!props.canManageContacts) return
      flushBlurAutoVerifyTimers()
      const normalized = String(groupId ?? '').trim()
      if (!normalized) return
      if (localValue.value.includes(normalized)) return
      // На мобилке группу добавляем перед draft-полем, если оно пустое.
      if (isMobileLayout.value) {
        const last = localValue.value.length - 1
        const lastEmpty = last >= 0 && !String(localValue.value[last] ?? '').trim()
        if (lastEmpty) {
          localValue.value.splice(last, 0, normalized)
        } else {
          localValue.value.push(normalized)
        }
      } else {
        localValue.value.push(normalized)
      }
      updateKeys()
      emit('input', serializeOutgoing(localValue.value))
    }

    const canAddItem = computed(() => {
      if (localValue.value.length === 0) return true
      return localValue.value.every((item) => String(item ?? '').trim().length > 0)
    })

    const getDuplicateKey = (item: string) => {
      const normalized = normalizeValue(item)
      if (!normalized) return ''
      const kind = getInputKind(normalized)
      if (kind === 'phone') return `phone:${normalizePhoneForStore(normalized)}`
      if (kind === 'tg_group') return `tg_group:${cleanTgGroupId(normalized)}`
      if (kind === 'tg_user') return `tg_user:${tgUsernameWithoutAt(normalized).toLowerCase()}`
      return `raw:${normalized.replace(/\s/g, '').toLowerCase()}`
    }

    const duplicateIndexMap = computed(() => {
      const map = new Map<string, number[]>()
      localValue.value.forEach((item, index) => {
        const key = getDuplicateKey(item)
        if (!key) return
        const indexes = map.get(key) || []
        indexes.push(index)
        map.set(key, indexes)
      })
      return map
    })
    const isDuplicateItem = (index: number) => {
      const key = getDuplicateKey(localValue.value[index])
      if (!key) return false
      const indexes = duplicateIndexMap.value.get(key) || []
      return indexes.length > 1
    }
    const addFromAllGroupsItemsPrepared = computed(() => {
      const selected = new Set(localValue.value.map((item) => normalizeValue(item)).filter(Boolean))
      return (props.addFromAllGroupsItems || []).map((item) => {
        const id = String(item?.id ?? '').trim()
        return {
          ...item,
          disabled: selected.has(id),
          disabledText: selected.has(id) ? 'Группа уже добавлена' : undefined,
        }
      })
    })

    const getItemMeta = (index: number) => {
      const meta = props.itemsMeta?.[index]
      if (meta?.verificationRequestFailed) {
        return { verificationState: 'request_error' as const }
      }
      if (typeof meta?.isVerified === 'boolean') {
        if (meta.isVerified) return { verificationState: true }
        if (meta.isVerificationChecked) return { verificationState: false }
      }
      return { verificationState: null }
    }
    const getItemDisplayName = (index: number) => {
      const meta = props.itemsMeta?.[index]
      const fromMeta = String(meta?.displayName ?? '').trim()
      if (fromMeta) return fromMeta

      const currentValue = normalizeValue(localValue.value[index] || '')
      if (!currentValue) return ''
      const matchedGroup = (props.addFromAllGroupsItems || []).find((item) => {
        const id = normalizeValue(String(item?.id ?? ''))
        return id === currentValue
      })
      return String(matchedGroup?.name ?? '').trim()
    }
    const getItemDisplayType = (index: number) => {
      const meta = props.itemsMeta?.[index]
      const type = String(meta?.contactType ?? '').trim().toLowerCase()
      if (type === 'tg_user' || type === 'tg_group' || type === 'phone') return type

      const currentValue = normalizeValue(localValue.value[index] || '')
      if (!currentValue) return 'empty'
      const kind = getInputKind(currentValue)
      if (kind === 'phone') return 'phone'
      if (kind === 'tg_group') return 'tg_group'
      if (kind === 'tg_user') return 'tg_user'
      if (kind === 'unknown') return 'unknown'
      return 'empty'
    }
    const getItemDisplayIcon = (index: number) => {
      const type = getItemDisplayType(index)
      if (type === 'tg_user') return 'mdi-account'
      if (type === 'tg_group') return 'mdi-account-group'
      if (type === 'phone') return 'mdi-phone'
      if (type === 'unknown') return 'mdi-alert-circle-outline'
      return 'mdi-card-account-details'
    }

    const isItemFilled = (item: string) => String(item ?? '').trim().length > 0

    const isLocallyValidForVerify = (item: string) => {
      const s = normalizeValue(item)
      const kind = getInputKind(s)
      if (kind === 'empty') return false
      if (kind === 'unknown') return false
      if (kind === 'phone') return isValidPhone(s)
      if (kind === 'tg_user') return isValidTgUsername(s)
      if (kind === 'tg_group') return isValidTgGroupId(s)
      return false
    }

    const canVerifyItem = (item: string, index: number) => {
      if (!isItemFilled(item)) return false
      if (isDuplicateItem(index)) return false
      return isLocallyValidForVerify(item)
    }

    const getCanonicalValueForRow = (index: number) =>
      canonicalFromRaw(String(localValue.value[index] ?? ''))

    const syncLastVerifiedFromMeta = () => {
      const list = props.itemsMeta || []
      const prev = lastVerifiedCanonicalByIndex.value
      const next = { ...prev }
      let changed = false
      list.forEach((meta, index) => {
        const m = meta as {
          isVerificationChecked?: boolean
          isVerified?: boolean | null
          contactValue?: string | null
        }
        if (Boolean(m?.isVerificationChecked) || m?.isVerified === true) {
          const canon = canonicalFromRaw(String(m?.contactValue ?? ''))
          if (next[index] !== canon) {
            next[index] = canon
            changed = true
          }
        }
      })
      if (changed) lastVerifiedCanonicalByIndex.value = next
    }

    watch(
      () =>
        (props.itemsMeta || []).map((meta) => ({
          checked: Boolean((meta as { isVerificationChecked?: boolean })?.isVerificationChecked),
          ok: (meta as { isVerified?: boolean | null })?.isVerified === true,
          cv: String((meta as { contactValue?: string | null })?.contactValue ?? ''),
        })),
      () => {
        syncLastVerifiedFromMeta()
      },
      { deep: true, immediate: true }
    )

    const armSkipBlurAutoVerifyForRow = (index: number) => {
      skipBlurAutoVerifyRowIndex = index
      if (skipBlurAutoVerifyClearTimer != null) clearTimeout(skipBlurAutoVerifyClearTimer)
      skipBlurAutoVerifyClearTimer = setTimeout(() => {
        skipBlurAutoVerifyClearTimer = null
        skipBlurAutoVerifyRowIndex = null
      }, 800)
    }

    const onInputFocus = (index: number) => {
      clearBlurAutoVerifyTimer(index)
    }

    const onInputBlur = (index: number, _e: FocusEvent) => {
      // На мобилке проверка только по кнопке «Проверить».
      if (isMobileLayout.value) return
      if (skipBlurAutoVerifyRowIndex === index) {
        skipBlurAutoVerifyRowIndex = null
        if (skipBlurAutoVerifyClearTimer != null) {
          clearTimeout(skipBlurAutoVerifyClearTimer)
          skipBlurAutoVerifyClearTimer = null
        }
        return
      }
      const item = localValue.value[index] || ''
      if (!props.canManageContacts) return
      if (getItemMeta(index).verificationState === true) return
      if (!canVerifyItem(item, index)) return
      const canon = getCanonicalValueForRow(index)
      const last = lastVerifiedCanonicalByIndex.value[index]
      if (last !== undefined && canon === last) return
      if (isRowVerifying(index)) return

      clearBlurAutoVerifyTimer(index)
      blurAutoVerifyTimers[index] = setTimeout(() => {
        delete blurAutoVerifyTimers[index]
        tryEmitBlurAutoVerify(index)
      }, BLUR_AUTO_VERIFY_MS)
    }

    const isLocalFormatInvalid = (item: string, index: number) => {
      if (!isItemFilled(item)) return false
      if (isDuplicateItem(index)) return false
      if (getItemMeta(index).verificationState === true) return false
      return !isLocallyValidForVerify(item)
    }

    const localFormatMessage = (item: string) => {
      const s = normalizeValue(item)
      const kind = getInputKind(s)
      if (kind === 'unknown') return UNKNOWN_FORMAT_MESSAGE
      if (kind === 'phone') {
        const msg = props.phoneValidator(s)
        return msg || 'Некорректный номер телефона'
      }
      if (kind === 'tg_user') {
        const r = tgUsernameValidator(s)
        return r === true ? 'Некорректный Telegram username' : r
      }
      if (kind === 'tg_group') {
        const r = tgGroupIdValidator(s)
        return typeof r === 'string' ? r : 'Некорректный id группы Telegram'
      }
      return UNKNOWN_FORMAT_MESSAGE
    }

    const unknownRowRule = (v: string) => {
      const t = String(v ?? '').trim()
      if (!t) return true
      if (getInputKind(t) === 'unknown') return UNKNOWN_FORMAT_MESSAGE
      return true
    }

    const tgGroupRowRule = (v: string) => {
      const t = String(v ?? '').trim()
      if (!t) return true
      const err = tgGroupIdValidator(t)
      return err === undefined ? true : err
    }

    const onGroupIdKeydown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const key = e.key
      const allowedControl = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab']
      if (allowedControl.includes(key)) return
      if (key >= '0' && key <= '9') return
      if (key === '-') {
        const target = e.target as HTMLInputElement | null
        const hasMinus = (target?.value || '').includes('-')
        const cursorAtStart = (target?.selectionStart ?? 0) === 0
        if (!hasMinus && cursorAtStart) return
      }
      e.preventDefault()
    }

    const onGroupIdPaste = (index: number, e: ClipboardEvent) => {
      e.preventDefault()
      const pasted = e.clipboardData?.getData('text') || ''
      const cleaned = cleanTgGroupId(pasted)
      updateItem(index, cleaned)
    }

    const onPhoneKeydown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const key = e.key
      const allowedControl = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab']
      if (allowedControl.includes(key)) return
      if (key >= '0' && key <= '9') return
      const target = e.target as HTMLInputElement | null
      if (isReplacingEntireInput(target) && isNonPhoneStarterKey(key)) return
      if (key === '+') {
        const value = target?.value || ''
        const start = target?.selectionStart ?? 0
        const replacesAll = isReplacingEntireInput(target)
        const atStart = start === 0
        const hasPlus = sanitizePhoneInput(value).startsWith('+')
        if ((atStart || replacesAll) && !hasPlus) return
      }
      e.preventDefault()
    }

    const onPhonePaste = (index: number, e: ClipboardEvent) => {
      e.preventDefault()
      const pasted = e.clipboardData?.getData('text') || ''
      updateItem(index, sanitizePhoneInput(pasted))
    }

    const getRowRule = (item: string) => {
      const kind = getInputKind(item)
      if (kind === 'tg_group') return tgGroupRowRule
      if (kind === 'unknown') return unknownRowRule
      if (kind === 'tg_user') {
        return (v: string) => {
          const t = String(v ?? '').trim()
          if (!t) return true
          const res = tgUsernameValidator(stripTgUsernameWhitespace(t))
          return res === true ? true : res
        }
      }
      if (kind === 'phone') {
        return (v: string) => {
          const t = String(v ?? '').trim()
          if (!t) return true
          const normalized = normalizePhoneForStore(t)
          return props.phoneValidator(normalized) || true
        }
      }
      return () => true
    }

    const onInputKeydown = (index: number, e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const item = localValue.value[index] || ''
        if (props.canManageContacts && canVerifyItem(item, index) && !isRowVerifying(index)) {
          if (isMobileLayout.value) {
            pendingToastIndex.value = index
          }
          emit('verify', index)
        }
        e.preventDefault()
        return
      }
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault()
        return
      }
      const currentInputValue = getCurrentInputValue(index, e)
      const kind = getInputKind(currentInputValue)
      if (kind === 'phone') {
        onPhoneKeydown(e)
        return
      }
      if (kind === 'tg_group') {
        onGroupIdKeydown(e)
      }
    }

    const onInputPaste = (index: number, e: ClipboardEvent) => {
      const pasted = (e.clipboardData?.getData('text') || '').replace(/\s/g, '')
      const pasteKind = getInputKind(pasted.trim())
      const currentKind = getInputKind(localValue.value[index] || '')

      if (pasteKind === 'tg_user' || pasteKind === 'tg_group') {
        e.preventDefault()
        updateItem(index, pasted)
        return
      }
      if (currentKind === 'phone' || pasteKind === 'phone') {
        onPhonePaste(index, e)
        return
      }
      if (currentKind === 'tg_group') {
        onGroupIdPaste(index, e)
        return
      }
      e.preventDefault()
      updateItem(index, pasted)
    }

    const isStarterPack = computed(() => props.appearance === 'starter-pack')

    const draftIndex = computed(() => {
      if (!localValue.value.length) return -1
      // На мобилке черновик — всегда последняя строка.
      return localValue.value.length - 1
    })

    const savedIndices = computed(() => {
      const last = localValue.value.length - 1
      if (last < 0) return []
      // Всё до черновика (последней строки) — в списке снизу, в т.ч. с неудачной проверкой.
      return localValue.value
        .slice(0, last)
        .map((item, index) => (isItemFilled(item) ? index : -1))
        .filter((index) => index >= 0)
    })

    const pushMobileDraftRow = () => {
      if (ensuringDraft) return
      ensuringDraft = true
      localValue.value.push('')
      updateKeys()
      emit('input', serializeOutgoing(localValue.value))
      nextTick(() => {
        ensuringDraft = false
      })
    }

    const ensureMobileDraftRow = () => {
      if (!isMobileLayout.value || !props.canManageContacts) return
      if (!localValue.value.length) {
        mobileDraftActive.value = false
        pushMobileDraftRow()
        return
      }
      const last = localValue.value.length - 1
      const lastFilled = isItemFilled(localValue.value[last] || '')
      const lastVerifiedOk = getItemMeta(last).verificationState === true

      // После успешной проверки — контакт уходит в список, открываем пустой черновик.
      if (lastVerifiedOk) {
        mobileDraftActive.value = false
        pushMobileDraftRow()
        return
      }

      // Уже есть пустой черновик.
      if (!lastFilled) {
        return
      }

      // Последняя строка заполнена, но это не активный черновик пользователя
      // (типичный кейс загрузки: несколько контактов с ошибкой проверки) —
      // все они остаются в списке снизу, в поле добавляем пустую строку.
      if (!mobileDraftActive.value) {
        pushMobileDraftRow()
      }
    }

    watch(
      [isMobileLayout, localValue, () => props.itemsMeta],
      () => {
        ensureMobileDraftRow()
      },
      { deep: true, immediate: true }
    )

    watch(isMobileLayout, (mobile, wasMobile) => {
      if (mobile && wasMobile === false) {
        // При переходе на мобильный layout сбрасываем «активный черновик»,
        // чтобы уже существующие строки ушли в список, а поле стало пустым.
        mobileDraftActive.value = false
        ensureMobileDraftRow()
      }
      if (!mobile) {
        mobileDraftActive.value = false
      }
    })

    const getSavedCardTone = (index: number): 'ok' | 'warn' | 'fail' => {
      if (isDuplicateItem(index)) return 'warn'
      const state = getItemMeta(index).verificationState
      if (state === true) return 'ok'
      if (state === false || state === 'request_error') return 'fail'
      if (isLocalFormatInvalid(localValue.value[index] || '', index)) return 'warn'
      return 'warn'
    }

    const getSavedCardStatusText = (index: number) => {
      if (isDuplicateItem(index)) return 'Такое значение уже добавлено'
      const state = getItemMeta(index).verificationState
      if (state === true) return 'Отправка доступна'
      if (state === 'request_error') return 'Не удалось выполнить проверку'
      if (state === false) return 'Отправка недоступна'
      if (isLocalFormatInvalid(localValue.value[index] || '', index)) {
        return localFormatMessage(localValue.value[index] || '')
      }
      return 'Требуется проверка'
    }

    const mobileDraftError = computed(() => {
      const index = draftIndex.value
      if (index < 0) return ''
      const item = localValue.value[index] || ''
      if (!isItemFilled(item)) return ''
      if (isDuplicateItem(index)) return 'Такое значение уже добавлено'
      if (isLocalFormatInvalid(item, index)) return localFormatMessage(item)
      const state = getItemMeta(index).verificationState
      if (state === 'request_error') return 'Не удалось выполнить проверку. Попробуйте ещё раз'
      if (state === false) return 'Отправка недоступна'
      return ''
    })

    const triggerAddedToast = () => {
      showAddedToast.value = true
      if (addedToastTimer != null) clearTimeout(addedToastTimer)
      addedToastTimer = setTimeout(() => {
        showAddedToast.value = false
        addedToastTimer = null
      }, ADDED_TOAST_MS)
    }

    const onMobileVerify = () => {
      const index = draftIndex.value
      if (index < 0) return
      if (!props.canManageContacts || !canVerifyItem(localValue.value[index] || '', index)) return
      if (isRowVerifying(index)) return
      pendingToastIndex.value = index
      // Пока идёт/завершается проверка — это всё ещё черновик (при ошибке остаётся в поле).
      mobileDraftActive.value = true
      emit('verify', index)
    }

    const onMobileRecheck = (index: number) => {
      if (!props.canManageContacts) return
      if (!canVerifyItem(localValue.value[index] || '', index)) return
      if (isRowVerifying(index)) return
      // Повторная проверка контакта из списка — без toast «добавлен» и без переноса в черновик.
      emit('verify', index)
    }

    watch(
      () => ({
        pending: pendingToastIndex.value,
        verifying: verifyingIndexList.value.slice(),
        meta: (props.itemsMeta || []).map((m) => ({
          ok: m?.isVerified === true,
          checked: Boolean(m?.isVerificationChecked),
          failed: Boolean(m?.verificationRequestFailed),
        })),
      }),
      ({ pending, verifying, meta }) => {
        if (pending == null) return
        if (verifying.includes(pending)) return
        const row = meta[pending]
        if (!row) {
          pendingToastIndex.value = null
          return
        }
        if (row.ok) {
          pendingToastIndex.value = null
          triggerAddedToast()
          return
        }
        if (row.failed || row.checked) {
          pendingToastIndex.value = null
        }
      },
      { deep: true }
    )

    onBeforeUnmount(() => {
      clearAllBlurAutoVerifyTimers()
      if (skipBlurAutoVerifyClearTimer != null) clearTimeout(skipBlurAutoVerifyClearTimer)
      if (addedToastTimer != null) clearTimeout(addedToastTimer)
    })

    return {
      isStarterPack,
      isMobileLayout,
      localValue,
      setInputRef,
      itemKeys,
      updateItem,
      removeItem,
      addItem,
      addGroupFromSelect,
      canAddItem,
      isDuplicateItem,
      addFromAllGroupsItemsPrepared,
      getItemMeta,
      getItemDisplayName,
      getItemDisplayType,
      getItemDisplayIcon,
      isItemFilled,
      isInputReadonly,
      isRowInputReadonly,
      isAnyRowVerifying,
      isRowVerifying,
      phoneValidator: props.phoneValidator,
      getInputKind,
      getInputDisplayValue,
      getInputLabel,
      getRowRule,
      canVerifyItem,
      isLocalFormatInvalid,
      localFormatMessage,
      unknownRowRule,
      tgGroupRowRule,
      onInputKeydown,
      onInputPaste,
      onInputFocus,
      onInputBlur,
      onAddActionPointerDown,
      armSkipBlurAutoVerifyForRow,
      draftIndex,
      savedIndices,
      mobileDraftError,
      showAddedToast,
      onMobileVerify,
      onMobileRecheck,
      getSavedCardTone,
      getSavedCardStatusText,
      savedListTitle: computed(() => props.savedListTitle),
    }
  },
})
